import { getI18nParams } from '@RootCommon/utils'
import WebVitalsSDK from '@sailor/web-vitals-sdk'
import { commonRequest as http } from '../api'
import mainApi from '../api/urls'

// 首页 URL 正则
export const HOME_URL_REG = /^https?:\/\/courier\.mykeeta(?:\.(?:test|st)\.sankuai)?\.com\/partner$/

// 首页标识
export const HOME_KEY = '3PL-HOME'

const region = getI18nParams().region

// 全局变量存储父子页面的指标数据
let parentMetrics: any = null
let iframeMetrics: any = null
let secondIframeMetrics: any = null
let hasReported = false
let iframeUrl: string | null = null
let secondIframeUrl: string | null = null
let currentUrl: string = window.location.href
const cityId = Number(sessionStorage.getItem('cityId'))

// 清空所有指标数据的函数
const clearAllMetrics = () => {
  parentMetrics = null
  iframeMetrics = null
  secondIframeMetrics = null
  hasReported = false
  iframeUrl = null
  secondIframeUrl = null
  console.log('已清空所有指标数据')
}

// 监听 iframe 发送的 Web Vitals 数据
const setupIframeMessageListener = () => {
  window.addEventListener('message', event => {
    if (event.origin !== window.location.origin && !event.origin.includes('sankuai.com') && !event.origin.includes('iframeOrigin')) {
      return
    }
    if (event.data && event.data.type === 'WEB_VITALS_REPORT') {
      const iframeData = event.data.data
      const iframeLevel = event.data.iframeLevel
      if (!iframeLevel || secondIframeMetrics) {
        iframeMetrics = {
          ...iframeData,
          source: event.data.source,
          subSource: event.data.subSource,
        }
        iframeUrl = window.location.href
        console.log('Web Vitals SDK: 收到 iframe 性能指标:', iframeMetrics)
      } else if (iframeLevel) {
        secondIframeMetrics = {
          ...iframeData,
        }
        secondIframeUrl = window.location.href
        console.log('Web Vitals SDK: 收到二级 iframe 性能指标:', secondIframeMetrics)
      }
      checkAndReport()
    }
  })
}

// 清理 URL 中的数字，用于统一分析
function cleanUrlForAnalysis (url: string): string {
  try {
    const targetUrl = url
    const urlObj = new URL(targetUrl)
    let cleanHash = urlObj.hash
    if (cleanHash.includes('?')) {
      cleanHash = cleanHash.split('?')[0]
    }
    cleanHash = cleanHash.replace(/\/\d+/g, '/{num}')
    let cleanPathname = urlObj.pathname.replace(/\/\d+/g, '/{num}')
    return urlObj.origin + cleanPathname + cleanHash
  } catch (error) {
    let fallbackUrl = url
    fallbackUrl = fallbackUrl.replace(/\/\d+/g, '/{num}')
    fallbackUrl = fallbackUrl.replace(/#/g, '*')
    return fallbackUrl
  }
}

// 合并 iframe 指标数据
function mergeIframeMetrics (): Record<string, any> {
  const allKeys = new Set([...(iframeMetrics ? Object.keys(iframeMetrics) : []), ...(secondIframeMetrics ? Object.keys(secondIframeMetrics) : [])])
  return Array.from(allKeys).reduce((acc, key) => {
    if (['source', 'iframeOrigin', 'iframeLevel', 'subSource'].includes(key)) return acc
    const grandsonValue = iframeMetrics && iframeMetrics[key] ? iframeMetrics[key] : 0
    const sonValue = secondIframeMetrics && secondIframeMetrics[key] ? secondIframeMetrics[key] : 0
    if (key === 'CLS') {
      acc[`iframe${key}`] = (sonValue + grandsonValue) / 2
    } else {
      acc[`iframe${key}`] = grandsonValue || sonValue
    }
    return acc
  }, {})
}
// 检查并上报指标（带重试机制）
const checkAndReport = async () => {
  if (hasReported) return

  const url = window.location.origin + window.location.pathname
  const isHomePage = HOME_URL_REG.test(url)

  // 首页处理
  if (isHomePage && parentMetrics) {
    const LCP = parentMetrics?.LCP || 0
    const reportData = {
      source: HOME_KEY,
      cityId,
      subSource: HOME_KEY,
      pageUrl: url,
      region,
      pageStartTime: 0,
      iframepageStartTime: 0,
      iframeLCP: LCP,
      ...(parentMetrics || {}),
    }
    try {
      await http.post(mainApi.performanceReport, reportData)
      console.log('Web Vitals SDK: 首页性能指标上报成功:', reportData)
      hasReported = true
      clearAllMetrics()
    } catch (error) {
      console.error('Web Vitals SDK: 首页指标上报失败:', error)
    }
    return
  }

  // 非首页处理 - 检查数据是否完整
  if (!parentMetrics) {
    console.log('Web Vitals SDK: 等待父页面指标收集完成...')
    return
  }
  if (!iframeMetrics) {
    console.log('Web Vitals SDK: 等待iframe指标收集完成...')
    return
  }

  // 数据完整，准备上报
  const combinedMetrics = {
    pageUrl: cleanUrlForAnalysis(iframeMetrics.topParentUrl || window.location.href),
    source: secondIframeMetrics?.source || iframeMetrics?.source,
    subSource: secondIframeMetrics?.subSource || iframeMetrics?.subSource,
    region,
    cityId,
    ...(parentMetrics || {}),
    ...mergeIframeMetrics(),
  }

  try {
    await http.post(mainApi.performanceReport, combinedMetrics)
    console.log('Web Vitals SDK: 性能指标上报成功:', combinedMetrics)
    hasReported = true
    clearAllMetrics()
  } catch (error) {
    console.error('Web Vitals SDK: 指标上报失败:', error)
  }
}

// 监听URL变化，重置指标数据
const setupUrlChangeListener = () => {
  window.addEventListener('popstate', () => {
    const newUrl = window.location.href
    if (newUrl !== currentUrl) {
      console.log('Web Vitals SDK: URL变化检测到，重置指标数据:', { oldUrl: currentUrl, newUrl: newUrl })
      clearAllMetrics()
      currentUrl = newUrl
    }
  })
  const originalPushState = history.pushState
  const originalReplaceState = history.replaceState
  history.pushState = function (...args) {
    originalPushState.apply(history, args)
    const newUrl = window.location.href
    if (newUrl !== currentUrl) {
      console.log('Web Vitals SDK: pushState URL变化检测到，重置指标数据:', { oldUrl: currentUrl, newUrl: newUrl })
      clearAllMetrics()
      currentUrl = newUrl
    }
  }
  history.replaceState = function (...args) {
    originalReplaceState.apply(history, args)
    const newUrl = window.location.href
    if (newUrl !== currentUrl) {
      console.log('Web Vitals SDK: replaceState URL变化检测到，重置指标数据:', { oldUrl: currentUrl, newUrl: newUrl })
      clearAllMetrics()
      currentUrl = newUrl
    }
  }
  window.addEventListener('hashchange', () => {
    const newUrl = window.location.href
    if (newUrl !== currentUrl) {
      console.log('Web Vitals SDK: hashchange URL变化检测到，重置指标数据:', { oldUrl: currentUrl, newUrl: newUrl })
      clearAllMetrics()
      currentUrl = newUrl
    }
  })
  window.addEventListener('beforeunload', () => {
    console.log('Web Vitals SDK: 页面即将卸载，清空指标数据')
    clearAllMetrics()
  })
}

export const initPerformanceReport = () => {
  setupIframeMessageListener()
  setupUrlChangeListener()
  new WebVitalsSDK({
    debug: true,
    realTimeReport: true,
    onReport: async data => {
      try {
        parentMetrics = { ...data }
        checkAndReport()
      } catch (error) {
        console.log('Web Vitals SDK: Reported metrics error', error)
      }
    },
  })
}
